/**
 * Media owner listings (§13.7).
 *
 * Only shown to vendors who actually own media — a flex printer has no sites
 * and the tab is hidden for them entirely (see Layout).
 *
 * The impression-method field is not optional decoration. §16.2 requires the
 * method to appear next to the number on the customer's screen, so the owner
 * is asked where their figure comes from and told plainly that it will be
 * shown. "45,000/day (traffic survey)" and "(owner estimate)" are different
 * claims, and the second one is not a lesser listing — it is an honest one.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type Listing, type ListingsResponse } from '../lib/api';
import { useI18n, useT, type StringKey } from '../lib/i18n';
import { formatINR, formatNumber } from '../lib/format';
import {
  ActionBar,
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Meta,
  PageHero,
  Spinner,
  type Tone,
} from '../components/Primitives';
import { Icon } from '../components/Icon';

const MEDIA_TYPES = [
  'ooh_hoarding',
  'ooh_society',
  'ooh_transit',
  'digital_screen',
  'newspaper',
  'radio',
  'cinema',
  'event_venue',
];

const RATE_UNITS = ['month', 'week', 'day', 'spot', 'insertion'] as const;

const IMPRESSION_METHODS = [
  { value: 'traffic_count', hi: 'ट्रैफ़िक सर्वे', en: 'Traffic survey' },
  { value: 'audited', hi: 'ऑडिट किया हुआ', en: 'Audited' },
  { value: 'circulation', hi: 'सर्कुलेशन', en: 'Circulation' },
  { value: 'listenership', hi: 'श्रोता संख्या', en: 'Listenership' },
  { value: 'estimate', hi: 'अपना अंदाज़ा', en: 'My own estimate' },
];

const STATUS_TONE: Record<string, Tone> = { live: 'good', draft: 'neutral', paused: 'warn' };

export function ListingsPage() {
  const { t } = useI18n();
  const T = useT();
  const [data, setData] = useState<ListingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.get<ListingsResponse>('/v1/partner/listings'));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.somethingWrong'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (adding) {
    return (
      <AddListing
        onDone={() => {
          setAdding(false);
          void load();
        }}
        onCancel={() => setAdding(false)}
      />
    );
  }

  if (importing)
    return (
      <ImportSheet
        onDone={() => {
          setImporting(false);
          void load();
        }}
      />
    );

  if (loading && !data) return <Spinner />;
  if (error) return <ErrorNote message={error} onRetry={() => void load()} />;

  const listings = data?.listings ?? [];

  const live = listings.filter((l) => l.status === 'live').length;
  return (
    <div className="space-y-4">
      <PageHero
        icon="listings"
        title={t('listings.title')}
        sub={T(
          'आपकी साइटें — रेट, खाली तारीखें और बुकिंग, एक जगह।',
          'Your sites — rates, free dates and bookings, in one place.',
        )}
        stats={[
          { label: T('कुल साइट', 'Sites'), value: String(listings.length) },
          { label: T('चालू', 'Live'), value: String(live) },
          {
            label: T('बुक दिन', 'Booked days'),
            value: String(listings.reduce((n, l) => n + l.bookedDays, 0)),
          },
        ]}
      />

      {listings.length === 0 ? (
        <EmptyState
          icon="listings"
          title={t('listings.title')}
          body={t('listings.empty')}
          action={
            <>
              <button type="button" className="btn-primary" onClick={() => setAdding(true)}>
                {t('listings.add')}
              </button>
              <ImportLink onClick={() => setImporting(true)} />
            </>
          }
        />
      ) : (
        <>
          <ul className="space-y-3">
            {listings.map((listing) => (
              <li key={listing.id}>
                <ListingCard listing={listing} onChanged={() => void load()} />
              </li>
            ))}
          </ul>

          <button type="button" className="btn-primary" onClick={() => setAdding(true)}>
            <Icon name="plus" className="h-5 w-5" strokeWidth={2.4} />
            {t('listings.add')}
          </button>
          <ImportLink onClick={() => setImporting(true)} />
        </>
      )}
    </div>
  );
}

function ListingCard({ listing, onChanged }: { listing: Listing; onChanged: () => void }) {
  const { t, lang } = useI18n();
  const T = useT();
  const [busy, setBusy] = useState(false);

  async function setStatus(status: 'live' | 'paused') {
    setBusy(true);
    try {
      await api.post(`/v1/partner/listings/${listing.id}/status`, { status });
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  const statusKey = `listings.${listing.status}` as StringKey;

  return (
    <Card>
      <div className="flex items-start gap-3">
        <span className="icon-tile h-11 w-11">
          <Icon name="listings" className="h-6 w-6" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-[17px] font-bold leading-snug">{listing.title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <Badge tone={STATUS_TONE[listing.status] ?? 'neutral'}>{t(statusKey)}</Badge>
            <Meta icon="listings">{listing.city}</Meta>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <div className="rounded-2xl px-3 py-2.5" style={{ background: 'var(--sec-wash)' }}>
          <p className="text-[11px] font-semibold text-muted">{T('रेट', 'Rate')}</p>
          <p
            className="tnum font-display text-lg font-bold"
            style={{ color: 'var(--sec-ink)' }}
          >
            {formatINR(listing.rateMicros)}
            <span className="text-xs font-medium text-muted">/{listing.rateUnit}</span>
          </p>
        </div>
        <div className="rounded-2xl bg-panel-2 px-3 py-2.5">
          <p className="text-[11px] font-semibold text-muted">{T('बुक', 'Booked')}</p>
          <p className="tnum font-display text-lg font-bold">
            {listing.bookedDays}{' '}
            <span className="text-xs font-medium text-muted">{T('दिन', 'days')}</span>
          </p>
        </div>
      </div>

      {listing.estDailyImpressions !== null && (
        <p className="mt-3 flex flex-wrap items-center gap-1 text-sm text-muted">
          <Icon name="team" className="h-4 w-4" />
          {formatNumber(listing.estDailyImpressions)} {T('लोग/दिन', 'people a day')}
          {listing.impressionMethod && (
            <span className="text-muted-dim">
              {' '}
              (
              {IMPRESSION_METHODS.find((m) => m.value === listing.impressionMethod)?.[lang] ??
                listing.impressionMethod}
              )
            </span>
          )}
        </p>
      )}

      <DatesToggle listingId={listing.id} />

      {listing.status !== 'draft' && (
        <button
          type="button"
          className="btn-secondary mt-3"
          disabled={busy}
          onClick={() => void setStatus(listing.status === 'live' ? 'paused' : 'live')}
        >
          {listing.status === 'live' ? t('listings.pauseIt') : t('listings.resumeIt')}
        </button>
      )}
    </Card>
  );
}

function AddListing({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const { t, lang } = useI18n();

  const [title, setTitle] = useState('');
  const [mediaType, setMediaType] = useState('ooh_hoarding');
  const [city, setCity] = useState('Meerut');
  const [widthFt, setWidthFt] = useState('');
  const [heightFt, setHeightFt] = useState('');
  const [illuminated, setIlluminated] = useState(false);
  const [rate, setRate] = useState('');
  const [rateUnit, setRateUnit] = useState<(typeof RATE_UNITS)[number]>('month');
  const [impressions, setImpressions] = useState('');
  const [method, setMethod] = useState('estimate');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOoh = mediaType.startsWith('ooh_');

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/v1/partner/listings', {
        mediaType,
        title: title.trim(),
        city,
        state: ['Dehradun', 'Haridwar', 'Roorkee'].includes(city)
          ? 'Uttarakhand'
          : 'Uttar Pradesh',
        ...(widthFt ? { widthFt: Number(widthFt) } : {}),
        ...(heightFt ? { heightFt: Number(heightFt) } : {}),
        ...(isOoh ? { illuminated } : {}),
        rate: rate.replace(/[^\d.]/g, ''),
        rateUnit,
        ...(impressions ? { estDailyImpressions: Number(impressions) } : {}),
        ...(impressions ? { impressionMethod: method } : {}),
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.somethingWrong'));
      setBusy(false);
    }
  }

  return (
    <div>
      <button
        type="button"
        className="-ml-1 mb-2 inline-flex min-h-[40px] items-center gap-1 text-sm font-semibold"
        style={{ color: 'var(--sec-ink)' }}
        onClick={onCancel}
      >
        <Icon name="back" className="h-4 w-4" strokeWidth={2.4} />
        {t('common.back')}
      </button>

      <h1 className="mb-4 font-display text-[24px] font-bold tracking-tight">
        {t('listings.add')}
      </h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="mt-4 space-y-4"
      >
        <Card className="space-y-4">
          <Field label={t('listings.titleField')}>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="NH-58 bypass"
            />
          </Field>

          <Field label={t('listings.mediaType')}>
            <select value={mediaType} onChange={(e) => setMediaType(e.target.value)}>
              {MEDIA_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t('register.city')}>
            <select value={city} onChange={(e) => setCity(e.target.value)}>
              {['Meerut', 'Ghaziabad', 'Noida', 'Muzaffarnagar', 'Lucknow', 'Dehradun'].map(
                (c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ),
              )}
            </select>
          </Field>

          {isOoh && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label={`${t('listings.size')} W`}>
                  <input
                    inputMode="decimal"
                    value={widthFt}
                    onChange={(e) => setWidthFt(e.target.value.replace(/[^\d.]/g, ''))}
                    className="tnum"
                  />
                </Field>
                <Field label={`${t('listings.size')} H`}>
                  <input
                    inputMode="decimal"
                    value={heightFt}
                    onChange={(e) => setHeightFt(e.target.value.replace(/[^\d.]/g, ''))}
                    className="tnum"
                  />
                </Field>
              </div>

              {/* Drives the print spec the platform raises against a booking:
                  an illuminated site gets 440gsm backlit material. */}
              <label className="flex min-h-touch items-center gap-3">
                <input
                  type="checkbox"
                  checked={illuminated}
                  onChange={(e) => setIlluminated(e.target.checked)}
                  className="h-6 w-6 accent-brand"
                  style={{ minHeight: 0 }}
                />
                <span>{t('listings.illuminated')}</span>
              </label>
            </>
          )}
        </Card>

        <Card className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('listings.rate')}>
              <input
                inputMode="decimal"
                value={rate}
                onChange={(e) => setRate(e.target.value.replace(/[^\d.]/g, ''))}
                className="tnum"
                placeholder="30000"
              />
            </Field>
            <Field label={t('listings.rateUnit')}>
              <select
                value={rateUnit}
                onChange={(e) => setRateUnit(e.target.value as (typeof RATE_UNITS)[number])}
              >
                {RATE_UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </Card>

        <Card className="space-y-4">
          <Field label={`${t('listings.impressions')} (${t('common.optional')})`}>
            <input
              inputMode="numeric"
              value={impressions}
              onChange={(e) => setImpressions(e.target.value.replace(/\D/g, ''))}
              className="tnum"
              placeholder="45000"
            />
          </Field>

          {impressions && (
            <Field
              label={t('listings.impressionMethod')}
              hint={t('listings.impressionMethodHint')}
            >
              <select value={method} onChange={(e) => setMethod(e.target.value)}>
                {IMPRESSION_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m[lang]}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </Card>

        {error && <p className="text-sm text-bad">{error}</p>}

        <ActionBar>
          <button type="submit" className="btn-primary" disabled={busy || !title || !rate}>
            {busy ? t('common.loading') : t('common.save')}
          </button>
        </ActionBar>
      </form>
    </div>
  );
}

function ImportLink({ onClick }: { onClick: () => void }) {
  const T = useT();
  return (
    <button type="button" className="btn-quiet mx-auto mt-3 block text-sm" onClick={onClick}>
      {T('बहुत सारी साइट? शीट से एक साथ जोड़ें', 'Many sites? Add them all from a sheet')}
    </button>
  );
}

const SHEET_HEADER =
  'title,media_type,city,state,locality,width_ft,height_ft,illuminated,rate,rate_unit,est_daily_impressions,latitude,longitude';

/** Many sites at once: a spreadsheet saved as CSV, or pasted. */
function ImportSheet({ onDone }: { onDone: () => void }) {
  const T = useT();
  const [csv, setCsv] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    added: number;
    problems: { row: number; message: string }[];
    live: boolean;
  } | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      setResult(await api.post('/v1/partner/listings/import', { csv }));
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : T('कुछ गड़बड़ हुई।', 'Something went wrong.'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="font-display text-[24px] font-bold tracking-tight">
        {T('शीट से साइट जोड़ें', 'Add sites from a sheet')}
      </h1>
      <Card className="space-y-3">
        <p className="text-sm text-muted">
          {T(
            'Excel या Google Sheet में पहली लाइन में ये कॉलम रखें, फिर “CSV” में सेव करके यहाँ चुनें — या पूरी शीट कॉपी करके नीचे चिपकाएँ।',
            'Put these columns in the first row of an Excel or Google Sheet, save it as CSV and choose it here — or copy the sheet and paste it below.',
          )}
        </p>
        <p className="break-all rounded-lg bg-panel-2 p-2 font-mono text-xs">{SHEET_HEADER}</p>
        <a
          className="btn-quiet text-sm"
          download="lulu-sites.csv"
          href={`data:text/csv;charset=utf-8,${encodeURIComponent(`${SHEET_HEADER}\nAbu Lane Chauraha,ooh_hoarding,Meerut,Uttar Pradesh,Abu Lane,40,20,yes,30000,month,45000,,\n`)}`}
        >
          {T('नमूना शीट डाउनलोड करें', 'Download a sample sheet')}
        </a>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void f.text().then(setCsv);
          }}
        />
        <textarea
          rows={6}
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          placeholder={SHEET_HEADER}
          className="font-mono text-xs"
        />
        {error && <p className="text-sm text-offline">{error}</p>}
        {result && (
          <div className="rounded-lg bg-panel-2 p-3 text-sm">
            <p className="font-semibold">
              {T(`${result.added} साइट जुड़ गईं`, `${result.added} sites added`)}
              {!result.live && T(' — जाँच के बाद दिखेंगी', ' — they show once you’re verified')}
            </p>
            {result.problems.map((p) => (
              <p key={p.row} className="text-xs text-offline">
                {T('लाइन', 'Row')} {p.row}: {p.message}
              </p>
            ))}
          </div>
        )}
      </Card>
      <ActionBar>
        {result ? (
          <button type="button" className="btn-primary" onClick={onDone}>
            {T('हो गया', 'Done')}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={busy || csv.trim().length < 10}
              onClick={() => void submit()}
            >
              {T('साइट जोड़ें', 'Add the sites')}
            </button>
            <button type="button" className="btn-secondary" onClick={onDone}>
              {T('वापस', 'Back')}
            </button>
          </>
        )}
      </ActionBar>
    </div>
  );
}

interface Period {
  id: string;
  startsOn: string;
  endsOn: string;
  state: string;
  bookingId: string | null;
}

/** Which dates are booked or blocked, and blocking dates you can’t sell. */
function DatesToggle({ listingId }: { listingId: string }) {
  const T = useT();
  const [open, setOpen] = useState(false);
  const [periods, setPeriods] = useState<Period[] | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const load = () =>
    api.get<{ periods: Period[] }>(`/v1/partner/listings/${listingId}/calendar`).then(
      (r) => setPeriods(r.periods),
      () => setPeriods([]),
    );
  const run = async (fn: () => Promise<unknown>) => {
    setMsg(null);
    try {
      await fn();
      void load();
    } catch (err) {
      setMsg(
        err instanceof ApiError ? err.message : T('कुछ गड़बड़ हुई।', 'Something went wrong.'),
      );
    }
  };
  if (!open)
    return (
      <button
        type="button"
        className="btn-soft mt-3"
        onClick={() => {
          setOpen(true);
          void load();
        }}
      >
        <Icon name="bookings" className="h-5 w-5" />
        {T('तारीखें देखें / बंद करें', 'See dates / block dates')}
      </button>
    );
  const label = (s: string) =>
    s === 'blocked'
      ? T('बंद (आपने)', 'Blocked (you)')
      : s === 'held'
        ? T('रोका गया', 'On hold')
        : T('बुक', 'Booked');
  return (
    <div className="mt-3 rounded-xl bg-panel-2 p-3">
      {!periods ? (
        <Spinner />
      ) : periods.length === 0 ? (
        <p className="text-sm text-muted">
          {T('सारी तारीखें खाली हैं।', 'All dates are free.')}
        </p>
      ) : (
        <ul className="space-y-1">
          {periods.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="tnum">
                {p.startsOn} → {p.endsOn}
              </span>
              <span className="flex items-center gap-2">
                <Badge
                  tone={
                    p.state === 'blocked' ? 'neutral' : p.state === 'held' ? 'warn' : 'good'
                  }
                >
                  {label(p.state)}
                </Badge>
                {p.state === 'blocked' && (
                  <button
                    type="button"
                    className="btn-quiet text-xs"
                    onClick={() =>
                      void run(() =>
                        api.post(`/v1/partner/listings/${listingId}/unblock`, {
                          blockId: p.id,
                        }),
                      )
                    }
                  >
                    {T('खोलें', 'Unblock')}
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="field-label mt-3">
        {T(
          'ये तारीखें बंद करें (मरम्मत, अपना ग्राहक…)',
          'Block these dates (repairs, your own client…)',
        )}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          aria-label={T('से', 'From')}
        />
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          aria-label={T('तक', 'To')}
        />
      </div>
      <button
        type="button"
        className="btn-secondary mt-2"
        disabled={!from || !to}
        onClick={() =>
          void run(async () => {
            await api.post(`/v1/partner/listings/${listingId}/block`, {
              startsOn: from,
              endsOn: to,
            });
            setFrom('');
            setTo('');
          })
        }
      >
        {T('बंद करें', 'Block')}
      </button>
      {msg && <p className="mt-2 text-sm text-offline">{msg}</p>}
    </div>
  );
}
